/**
 * Revision history with word diff. Selecting a revision shows what separates it from the page
 * as it reads now — additions in green, removals struck through.
 */
import { useEffect, useState } from "react";

import { api, type RevisionListItem } from "../../api";
import { Num } from "../../components/primitives";
import { SkeletonText } from "../../components/Skeleton";
import { diffWords, type DiffPart } from "./diff";

export function HistoryPanel({ pageId, currentText }: { pageId: string; currentText: string }) {
  const [revisions, setRevisions] = useState<RevisionListItem[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [parts, setParts] = useState<DiffPart[] | null>(null);

  useEffect(() => {
    setRevisions(null);
    setSelected(null);
    setParts(null);
    void api.revisions(pageId).then(setRevisions);
  }, [pageId]);

  useEffect(() => {
    if (selected === null) {
      setParts(null);
      return;
    }
    void api.revision(pageId, selected).then((revision) => {
      setParts(diffWords(revision.text_cache, currentText));
    });
  }, [pageId, selected, currentText]);

  if (revisions === null) {
    return <SkeletonText lines={3} />;
  }
  if (revisions.length === 0) {
    return <p className="text-small text-muted">Ingen historik ännu.</p>;
  }

  return (
    <div className="grid gap-s5">
      <ul className="grid gap-s2" aria-label="Revisioner">
        {revisions.map((revision) => (
          <li key={revision.revision}>
            <button
              type="button"
              onClick={() =>
                setSelected(selected === revision.revision ? null : revision.revision)
              }
              aria-pressed={selected === revision.revision}
              className={`flex w-full items-baseline gap-s4 rounded-control px-s4 py-s3 text-left text-small transition-[background] duration-control ease-nb ${
                selected === revision.revision ? "bg-surface-2" : "hover:bg-surface-2"
              }`}
            >
              <Num>v{revision.revision}</Num>
              <span className="flex-1 truncate text-ink">{revision.title}</span>
              <span className="text-muted">
                {revision.author ?? "—"} ·{" "}
                <Num>{new Date(revision.created_at).toLocaleString("sv-SE")}</Num>
              </span>
            </button>
          </li>
        ))}
      </ul>

      {selected !== null && (
        <div className="rounded-card border border-line bg-surface-sunken p-s5">
          <p className="nb-overline mb-s4">Skillnad: v{selected} → senast sparad</p>
          {parts === null ? (
            <SkeletonText lines={2} />
          ) : (
            <p className="whitespace-pre-wrap text-body leading-relaxed">
              {parts.map((part, index) =>
                part.kind === "same" ? (
                  <span key={index}>{part.text}</span>
                ) : part.kind === "added" ? (
                  <ins key={index} className="bg-pass-bg text-pass-text no-underline">
                    {part.text}
                  </ins>
                ) : (
                  <del key={index} className="bg-fail-bg text-fail-text">
                    {part.text}
                  </del>
                ),
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
